import React, { memo, useEffect, useState } from "react";
import KuikaSvg from "../../../../ui-kit/kuika-svg/kuika-svg";
import { McpToolTranscript, useStickyLabel } from "./mcp-chat-tool-transcript-view";
import { ToolActivity } from "./mcp-tool-activity";

interface McpChatThinkingIndicatorProps {
  toolActivities: ToolActivity[];
  /** Monotonic start time from performance.now() for live elapsed timer */
  startTime?: number;
  /**
   * Server-narrated phase for the turn in flight — "Planning pages (1 of 2)",
   * "Setting up tables (1 of 3)", "Designing ProductList". When set it replaces
   * the static Thinking/Working header, which changed only when the build handed
   * over between specialists and so could sit unchanged for minutes. Falls back
   * to Thinking/Working before the server narrates anything.
   */
  phaseTitle?: string | null;
  /**
   * The backend tool running right now. Passed straight through to the
   * transcript, which uses it to label its own line — backend tools are kept
   * off the transcript itself (KOA-17717), so they have no group to name it
   * with. Keeping it off the header is the point: the header holds the stage,
   * which is what makes it stable.
   */
  toolTitle?: string | null;
}

/** Minimum time a phase title stays on screen, so a fast run cannot strobe. */
const PHASE_STICKY_MS: number = 650;

function formatElapsed(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  if (totalSeconds < 60) return `${totalSeconds}s`;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}m ${seconds.toString().padStart(2, "0")}s`;
}

/** Ticks every second using setInterval + performance.now() for a live elapsed display. */
function useElapsedTimer(startTime?: number): string | null {
  const [display, setDisplay] = useState<string | null>(null);

  useEffect(() => {
    if (startTime == null) {
      setDisplay(null);
      return;
    }

    // Immediate first read
    setDisplay(formatElapsed(performance.now() - startTime));

    const id = setInterval(() => {
      setDisplay(formatElapsed(performance.now() - startTime));
    }, 200);

    return () => clearInterval(id);
  }, [startTime]);

  return display;
}

const McpChatThinkingIndicator: React.FC<McpChatThinkingIndicatorProps> = ({
  toolActivities,
  startTime,
  phaseTitle,
  toolTitle
}) => {
  const elapsed = useElapsedTimer(startTime);
  const hasActivities: boolean = toolActivities.length > 0;

  // Claude-style fallback: "Thinking…" before any tool runs, "Working…" once
  // tools are executing. Used only until the server names a phase.
  const fallbackText: string = hasActivities ? "Working" : "Thinking";
  const rawHeader: string = phaseTitle != null && phaseTitle.length > 0 ? phaseTitle : fallbackText;
  const headerText: string = useStickyLabel(rawHeader, PHASE_STICKY_MS);

  return (
    <div className="mcp-chat__message mcp-chat__message--assistant">
      <div className="mcp-chat__avatar">
        <KuikaSvg name="sparkles" type="datasource-icons" width={14} height={14} />
      </div>
      <div className="mcp-chat__thinking">
        <div className="mcp-chat__thinking-head">
          {/* Server phases run longer than "Thinking…" and the row ellipsises
              them; keep the full text reachable on hover. */}
          <span className="mcp-chat__thinking-text" title={headerText}>
            {headerText}…
          </span>
          {elapsed && (
            <span className="mcp-chat__thinking-elapsed">
              <span className="mcp-chat__thinking-elapsed-dot" />
              {elapsed}
            </span>
          )}
        </div>
        {hasActivities && (
          <McpToolTranscript
            activities={toolActivities}
            mode="live"
            showDetails={false}
            chatActive={true}
            backendToolLabel={toolTitle}
          />
        )}
      </div>
    </div>
  );
};

export default memo(McpChatThinkingIndicator);
